import { useState, useRef } from 'react';
import DataTable from './DataTable';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Nombre' },
  { key: 'username', label: 'Usuario' },
  { key: 'rol', label: 'Rol' },
  { key: 'aceptacion_habeas', label: 'Habeas Data' },
  { key: 'fecha_habeas_data', label: 'Fecha Habeas Data' },
  { key: 'created_at', label: 'Creado' },
];

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px',
  color: theme.colors.text,
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  color: theme.colors.textMuted,
  marginBottom: '0.4rem',
  fontSize: '0.85rem',
};

// Roles que NO se pueden editar ni eliminar (administradores)
const ADMIN_ROLES = [1, 2];

function Users({ token }) {
  const isMobile = useIsMobile();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', username: '', password: '', role_id: '3' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  // Edición de rol
  const [editUser, setEditUser] = useState(null);
  const [editRole, setEditRole] = useState('3');
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  // Borrado
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const tableRef = useRef(null);

  const openModal = () => {
    setForm({ username: '', password: '', role_id: '3' });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    if (formLoading) return;
    setShowModal(false);
    setFormError('');
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditRole(String(user.role_id));
    setEditError('');
  };

  const closeEdit = () => {
    if (editLoading) return;
    setEditUser(null);
    setEditError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError('');
    setEditLoading(true);
    try {
      const res = await fetch(`/api/users/${editUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role_id: Number(editRole) }),
      });
      const data = await res.json();
      if (!res.ok) {
        setEditError(data.message || 'Error al actualizar usuario');
        return;
      }
      setEditUser(null);
      setSuccessMsg(`Usuario "${editUser.username}" actualizado exitosamente.`);
      setTimeout(() => setSuccessMsg(''), 4000);
      if (tableRef.current) tableRef.current.reload();
    } catch {
      setEditError('Error de conexión');
    } finally {
      setEditLoading(false);
    }
  };

  const openDelete = (user) => {
    setDeleteUser(user);
    setDeleteError('');
  };

  const closeDelete = () => {
    if (deleteLoading) return;
    setDeleteUser(null);
    setDeleteError('');
  };

  const handleDelete = async () => {
    setDeleteError('');
    setDeleteLoading(true);
    try {
      const res = await fetch(`/api/users/${deleteUser.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setDeleteError(data.message || 'Error al eliminar usuario');
        return;
      }
      const removed = deleteUser.username;
      setDeleteUser(null);
      setSuccessMsg(`Usuario "${removed}" eliminado exitosamente.`);
      setTimeout(() => setSuccessMsg(''), 4000);
      if (tableRef.current) tableRef.current.reload();
    } catch {
      setDeleteError('Error de conexión');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.username)) {
      setFormError('El usuario debe ser un correo electrónico válido (ej: nombre@dominio.com)');
      return;
    }
    if (form.password.length < 6) {
      setFormError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setFormLoading(true);
    try {
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, role_id: Number(form.role_id) }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.message || 'Error al crear usuario');
        return;
      }
      setShowModal(false);
      setSuccessMsg(`Usuario "${form.username}" creado exitosamente.`);
      setTimeout(() => setSuccessMsg(''), 4000);
      if (tableRef.current) tableRef.current.reload();
    } catch {
      setFormError('Error de conexión');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <main style={{ padding: isMobile ? '1.25rem 1rem' : '2rem 1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '14px',
        border: `1px solid ${theme.colors.border}`,
        padding: isMobile ? '1.25rem' : '2rem',
        boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ color: theme.colors.primary, fontSize: '1.5rem', marginBottom: '0.3rem' }}>
              Usuarios
            </h1>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem' }}>
              Usuarios registrados en tu empresa.
            </p>
          </div>
          <button
            onClick={openModal}
            style={{
              padding: '9px 20px',
              backgroundColor: theme.colors.primary,
              color: theme.colors.background,
              border: 'none',
              borderRadius: '7px',
              fontSize: '0.9rem',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            + Crear Usuario
          </button>
        </div>

        {successMsg && (
          <div style={{
            backgroundColor: 'rgba(74,222,128,0.1)',
            border: `1px solid ${theme.colors.success}`,
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            marginBottom: '1.5rem',
            color: theme.colors.success,
            fontSize: '0.9rem',
          }}>
            {successMsg}
          </div>
        )}

        <DataTable
          ref={tableRef}
          columns={COLUMNS}
          fetchUrl="/api/users"
          token={token}
          renderActions={(row) => {
            // Los administradores no se pueden editar ni eliminar:
            // la empresa siempre conserva al menos un administrador.
            if (ADMIN_ROLES.includes(row.role_id)) {
              return <span style={{ color: theme.colors.textMuted, fontSize: '0.8rem' }}>—</span>;
            }
            return (
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                <button
                  onClick={() => openEdit(row)}
                  title="Editar rol"
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontSize: '0.82rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: 'transparent',
                    color: theme.colors.primary,
                  }}
                >
                  Editar
                </button>
                <button
                  onClick={() => openDelete(row)}
                  title="Eliminar usuario"
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontSize: '0.82rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    border: `1px solid ${theme.colors.error}`,
                    backgroundColor: 'transparent',
                    color: theme.colors.error,
                  }}
                >
                  Borrar
                </button>
              </div>
            );
          }}
        />
      </div>

      {showModal && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 200,
            padding: isMobile ? '1rem' : 0,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: theme.colors.surface,
              borderRadius: '14px',
              border: `1px solid ${theme.colors.border}`,
              padding: isMobile ? '1.5rem' : '2rem',
              width: '100%',
              maxWidth: '400px',
              boxSizing: 'border-box',
              boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem' }}>Crear Usuario</h2>
              <button
                onClick={closeModal}
                style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={labelStyle}>Nombre</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Nombre que verán los clientes al agendar"
                  autoComplete="off"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '1.1rem' }}>
                <label style={labelStyle}>Correo electrónico</label>
                <input
                  type="email"
                  required
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                  autoComplete="off"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '1.1rem' }}>
                <label style={labelStyle}>Contraseña</label>
                <input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  autoComplete="new-password"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelStyle}>Rol</label>
                <select
                  value={form.role_id}
                  onChange={(e) => setForm((f) => ({ ...f, role_id: e.target.value }))}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="3">Trabajador</option>
                  <option value="4">Agente</option>
                </select>
              </div>

              {formError && (
                <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>
                  {formError}
                </p>
              )}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={formLoading}
                  style={{
                    flex: 1, padding: '10px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '7px',
                    color: theme.colors.textMuted,
                    fontSize: '0.9rem',
                    cursor: formLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  style={{
                    flex: 1, padding: '10px',
                    backgroundColor: formLoading ? theme.colors.border : theme.colors.primary,
                    border: 'none',
                    borderRadius: '7px',
                    color: theme.colors.background,
                    fontSize: '0.9rem',
                    fontWeight: '700',
                    cursor: formLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {formLoading ? 'Creando...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editUser && (
        <div
          onClick={closeEdit}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 200,
            padding: isMobile ? '1rem' : 0,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: theme.colors.surface,
              borderRadius: '14px',
              border: `1px solid ${theme.colors.border}`,
              padding: isMobile ? '1.5rem' : '2rem',
              width: '100%',
              maxWidth: '400px',
              boxSizing: 'border-box',
              boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem' }}>Editar Usuario</h2>
              <button
                onClick={closeEdit}
                style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginBottom: '1.1rem' }}>
                {editUser.name ? `${editUser.name} · ` : ''}{editUser.username}
              </p>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelStyle}>Rol</label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="3">Trabajador</option>
                  <option value="4">Agente</option>
                </select>
              </div>

              {editError && (
                <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>
                  {editError}
                </p>
              )}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={editLoading}
                  style={{
                    flex: 1, padding: '10px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '7px',
                    color: theme.colors.textMuted,
                    fontSize: '0.9rem',
                    cursor: editLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  style={{
                    flex: 1, padding: '10px',
                    backgroundColor: editLoading ? theme.colors.border : theme.colors.primary,
                    border: 'none',
                    borderRadius: '7px',
                    color: theme.colors.background,
                    fontSize: '0.9rem',
                    fontWeight: '700',
                    cursor: editLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {editLoading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteUser && (
        <div
          onClick={closeDelete}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 200,
            padding: isMobile ? '1rem' : 0,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: theme.colors.surface,
              borderRadius: '14px',
              border: `1px solid ${theme.colors.border}`,
              padding: isMobile ? '1.5rem' : '2rem',
              width: '100%',
              maxWidth: '400px',
              boxSizing: 'border-box',
              boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ color: theme.colors.error, fontSize: '1.2rem' }}>Eliminar Usuario</h2>
              <button
                onClick={closeDelete}
                style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <p style={{ color: theme.colors.text, fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              ¿Seguro que quieres eliminar a{' '}
              <strong>{deleteUser.name ? `${deleteUser.name} (${deleteUser.username})` : deleteUser.username}</strong>?
              Esta acción no se puede deshacer.
            </p>

            {deleteError && (
              <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>
                {deleteError}
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={closeDelete}
                disabled={deleteLoading}
                style={{
                  flex: 1, padding: '10px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '7px',
                  color: theme.colors.textMuted,
                  fontSize: '0.9rem',
                  cursor: deleteLoading ? 'not-allowed' : 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteLoading}
                style={{
                  flex: 1, padding: '10px',
                  backgroundColor: deleteLoading ? theme.colors.border : theme.colors.error,
                  border: 'none',
                  borderRadius: '7px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  cursor: deleteLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {deleteLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Users;
